import {
  type AddressComparisonFields,
  type GeocodePrecision,
  buildGeocodeInputText,
  departmentCentroidFromZip,
  hasAddressChanged,
  isExactLocalityMatch,
  isReliableGeocode,
  needsGeocodeConfirmation,
  normalizeAddressText,
} from "@mytour/shared";
import {useCallback, useEffect, useRef, useState} from "react";

// Exporté pour les formulaires consommateurs (#864), qui ont besoin du libellé et du score bruts.
export type GeocodeResult = {
  latitude: number;
  longitude: number;
  score: number;
  label: string;
  precision: GeocodePrecision | null;
  /** Nom de voie/lieu résolu par l'IGN, tel quel — base du containment saisie ↔ résolu (#920). Voir `IGeocode.resolvedName`. */
  resolvedName: string;
  /** Composants structurés (#980) — base de `structuredReplacement`. Voir `IGeocode`. */
  housenumber?: string;
  street?: string;
  postcode?: string;
  city?: string;
};

export type GetGeocode = (
  zip: string,
  city: string,
  address: string,
  address2?: string
) => Promise<GeocodeResult | null>;

export type ManualPinPosition = {lat: number; lng: number};

/**
 * Niveau de repli qui a résolu le centre de la carte actuellement affichée
 * (#1022) — posé par `openMap()` à chacun de ses paliers. Le tunnel public en
 * dérive un garde-fou visuel non bloquant sur les 2 derniers paliers ; les
 * formulaires authentifiés l'ignorent (scope tunnel-only, voir le câblage
 * optionnel sur `ManualPinPlacement`/`AddressVerificationSection`).
 */
export type MapCenterPrecision = "geocode" | "commune" | "department" | "default";

// Centre par défaut de la France, précédent `CustomersMap` — dernier repli de `openMap` (#1014).
const DEFAULT_MAP_CENTER: ManualPinPosition = {lat: 46.603354, lng: 1.888334};

/**
 * Niveau 2.5/3 du repli d'`openMap()` (#1022) — factorisé : appelé
 * identiquement quand le repli commune échoue (0 résultat) ET quand il lève
 * (catch), les deux mêmes issues. Département via `departmentCentroidFromZip`
 * (statique, zéro réseau) ; sinon `DEFAULT_MAP_CENTER` (France, dernier
 * recours, #1014).
 */
function resolveDepartmentOrDefaultMapCenter(zip: string): {
  center: ManualPinPosition;
  precision: MapCenterPrecision;
} {
  const departmentCentroid = departmentCentroidFromZip(zip);
  return departmentCentroid
    ? {
        center: {lat: departmentCentroid.latitude, lng: departmentCentroid.longitude},
        precision: "department",
      }
    : {center: DEFAULT_MAP_CENTER, precision: "default"};
}

/** Rue/CP/ville prêts à écraser la saisie (#980) — le complément n'y figure jamais, voir `structuredReplacement`. */
export type StructuredReplacementFields = {line1: string; zip: string; city: string};

/**
 * Verdict de résolution (#980, flow sans modale de confirmation ; élargi aux
 * lieux-dits par #1082) — pilote DIRECTEMENT le `disabled` du bouton de
 * soumission de l'appelant. Taxonomie à 4 états VISIBLES (`checking` est
 * transitoire, jamais un état affiché en soi) :
 * - `recognized` : precision `housenumber`/`street`/`locality`/`toponym` ET
 *   concordance saisie↔résolu (containment rue, ou égalité exacte pour un
 *   lieu-dit à un seul mot — `isExactLocalityMatch`, voir le commentaire sur
 *   `zipCityConcordant`/`status` plus bas).
 * - `close-match` : mêmes granularités MAIS divergence — callout
 *   « Adresse proche trouvée », `structuredReplacement` exploitable.
 * - `not-found` : tout le reste (0 résultat OU precision `municipality`/
 *   `unknown`) — carte checklist #799.
 * - `manual-pin` : pin gelé (`isManualFrozen`), toujours résolu.
 */
export type AddressResolutionStatus =
  | "checking"
  | "recognized"
  | "close-match"
  | "not-found"
  | "manual-pin";

/**
 * Pin manuel : la position ET les champs d'adresse AU MOMENT de la marque
 * (#929) — le gel (`isManualFrozen`) est dérivé en comparant ces champs à la
 * saisie courante, il retombe de lui-même dès que le texte change.
 */
export type ManualPinSeed = {position: ManualPinPosition; fields: AddressComparisonFields};

/** Moments de la confirmation bloquante mesurés par la télémétrie (#920). */
export type ConfirmationEvent = "shown" | "confirmed" | "rejected";

export type UseAddressVerificationOptions = {
  /** Saisie courante des champs d'adresse (valeurs `watch` du formulaire appelant). */
  fields: AddressComparisonFields;
  getGeocode: GetGeocode;
  /**
   * Position déjà marquée `manual` en base, appariée aux champs courants UNE
   * FOIS au montage (#929, « la fiche se souvient »). Pour un seed qui arrive
   * après le montage (données chargées en query), passer par `reset()`.
   */
  initialManualPin?: ManualPinPosition | null;
  /**
   * `false` = la vérification est hors écran (section repliée, modale fermée) :
   * aucun appel IGN n'est déclenché. L'état existant est conservé tel quel —
   * l'appelant qui ré-affiche repart via `reset()` s'il veut un état frais.
   */
  enabled?: boolean;
  /**
   * Télémétrie du callout « Adresse proche trouvée » (#920, re-mappée #980)
   * — émise ICI et pas chez l'appelant, pour rester structurellement
   * synchrone avec l'affichage réel. Câblée une seule fois par le wrapper
   * `@mytour/platform` (`geocodeConfirmationAnalytics`) : ce package-ci ne
   * dépend pas de la couche analytics. `"rejected"` n'est plus émis (#980 —
   * la modale qui le déclenchait ("Non") a disparu ; l'action de correction
   * n'est plus qu'une édition de champ, sans clic dédié à mesurer). Le type
   * garde la valeur pour ne pas casser `geocodeConfirmationAnalytics.rejected`
   * (`@mytour/platform`, hors périmètre) — mort en pratique, à retirer quand
   * ce call-site sera nettoyé.
   */
  onConfirmationEvent?: (event: ConfirmationEvent, precision: GeocodePrecision | null) => void;
};

export type GuardSubmitOptions = {
  /**
   * L'adresse a-t-elle été retouchée depuis le chargement (#864) : un
   * géocodage en vol ne bloque la soumission QUE dans ce cas — sinon toute
   * fiche existante imposerait un délai sur des champs sans rapport, le temps
   * du re-check au montage. Défaut `true` (bloquant), le cas conservateur.
   */
  editedSinceLoad?: boolean;
  /** La soumission passe. */
  onPass: () => void;
};

export type AddressVerification = {
  /** Saisie courante, telle que fournie par l'appelant — base de toutes les dérivations. */
  fields: AddressComparisonFields;
  /**
   * Résultat de géocodage POSÉ : `null` tant qu'une vérification est en vol —
   * un appelant qui gate une soumission dessus ne voit jamais un verdict
   * périmé pendant un re-check (#864).
   */
  geocode: GeocodeResult | null;
  processing: boolean;
  error: string;
  /** Verdict `isReliableGeocode` (#797) sur le résultat posé — `false` pendant une vérification. Sans lecteur applicatif depuis #980 (les 3 formulaires authentifiés lisent `resolved`/`status`, le nouveau verdict d'affichage/blocage) ; conservé pour un futur consommateur qui aurait besoin du critère de précision seule, distinct du verdict de résolution. */
  reliable: boolean;
  /**
   * Verdict de résolution (#980) — voir `AddressResolutionStatus`.
   * `AddressGeocodeStatus` en dérive le rendu, l'appelant en dérive `resolved`.
   */
  status: AddressResolutionStatus;
  /** `true` seulement pour `status` `"recognized"`/`"manual-pin"` — pilote directement le `disabled` du bouton de soumission de l'appelant. */
  resolved: boolean;
  /**
   * Rue ou lieu-dit /CP/ville prêts pour le bouton « Remplacer » du callout —
   * non-`null` dès que la precision est `housenumber`/`street`/`locality`/
   * `toponym` ET que les composants structurés sont présents (donc aussi en
   * `status: "recognized"`, pas seulement `"close-match"` : c'est
   * `AddressGeocodeStatus` qui restreint l'affichage du bouton au seul
   * `close-match` via son dispatch sur `status`, PAS ce champ). `line1` porte
   * la rue reconstituée (`housenumber`/`street`) ou, sur un lieu-dit, le
   * `resolvedName` tel quel (#1082) — le complément n'y figure jamais (le
   * géocodeur ne le connaît pas) : l'appelant qui écrit ce trio via
   * `onReplace` (prop de `AddressVerificationSection`) doit conserver le sien.
   */
  structuredReplacement: StructuredReplacementFields | null;
  /**
   * Télémétrie du clic « Remplacer » (`"confirmed"`) — l'écriture RÉELLE des
   * champs RHF reste chez l'appelant (le hook ne possède pas d'instance RHF,
   * même pattern que `payloadPin`/`dropPin`) : `AddressGeocodeStatus` appelle
   * cette méthode ET la prop `onReplace` au même clic.
   */
  replace: () => void;
  /** Le gel manuel est-il actif MAINTENANT (#929) — pin posé/seedé ET texte inchangé depuis. */
  isManualFrozen: boolean;
  /** Mode carte ouvert (#929) — l'appelant doit verrouiller ses champs texte tant que c'est vrai. */
  mapMode: boolean;
  /** Centre initial de la carte, figé à l'entrée en mode carte (jamais recentrée ensuite). */
  mapCenter: ManualPinPosition | null;
  /**
   * Niveau de repli qui a résolu `mapCenter` (#1022) — voir `MapCenterPrecision`.
   * `null` tant que la carte n'a jamais été ouverte cette session.
   */
  mapCenterPrecision: MapCenterPrecision | null;
  /**
   * Position COURANTE du pin posé cette session carte, `null` tant qu'aucun
   * drop n'a eu lieu (#1011) — à passer telle quelle (avec repli sur
   * `mapCenter`) au composant carte pour que le marqueur affiché suive
   * réellement le dernier drop, plutôt que de rester visuellement figé sur
   * `mapCenter`. Distinct de `payloadPin` (présence-based, retardé d'un
   * effet) : celui-ci doit être synchrone avec `dropPin` pour piloter un
   * rendu, `payloadPin` pilote un payload de sauvegarde.
   */
  pinPosition: ManualPinPosition | null;
  /**
   * Entre en mode carte, centré sur le géocode posé — ou, en échec total (0
   * résultat, aucun géocode posé), sur un géocodage best-effort de la seule
   * commune (#980, reprend le pattern pré-unification du tunnel : query
   * = le nom de ville, pas de rue) pour quand même cadrer la carte. Si ce
   * repli échoue aussi, centre du département dérivé du CP (statique, zéro
   * réseau, #1022) ; en tout dernier recours, centre par défaut de la France
   * (#1014) — n'échoue jamais fermé, sauf zip/ville absents
   * (`resolvingMapCenter` retombe alors à `false` sans jamais avoir démarré).
   * Le niveau qui a résolu est exposé par `mapCenterPrecision`.
   */
  openMap: () => void;
  /** `true` pendant le géocodage de secours de la commune (échec total) — pilote le spinner du CTA « Positionner sur la carte ». */
  resolvingMapCenter: boolean;
  /**
   * Abandonne la session carte SANS engager le pin qui y a été posé (#1011) :
   * restaure le `manualPin` tel qu'il était à l'entrée (`openMap()`), pas
   * seulement `mapMode`. La validation d'un pin fraîchement posé reste le
   * bouton Continuer/Enregistrer de l'appelant, jamais ce lien de retour.
   */
  closeMap: () => void;
  /**
   * Pose/ajuste la position du pin — répétable à volonté, la carte reste
   * ouverte (#1011). La validation reste un geste explicite de l'appelant
   * (bouton Continuer/Enregistrer, piloté par `resolved`/`payloadPin`), pas
   * ce callback.
   */
  dropPin: (position: ManualPinPosition) => void;
  removePin: () => void;
  /**
   * Trio à écrire dans le payload (#929, présence-based) : `undefined` tant
   * que rien n'a changé depuis le montage/`reset()` — un formulaire jamais
   * touché par le flux manuel n'envoie pas ces clés (distinguer « jamais
   * touché » de « retiré », #799). Ensuite : la position si le gel est actif,
   * `null` sinon (dé-pin explicite, y compris quand le texte est réédité
   * après un pin).
   */
  payloadPin: ManualPinPosition | null | undefined;
  /**
   * Message de correction affiché sous l'adresse — canal générique pour une
   * garde de soumission propre à la surface de l'appelant, distincte du
   * verdict de résolution (`status`/`resolved`, #980). Nettoyé automatiquement
   * dès qu'une nouvelle vérification démarre ou qu'un pin gèle l'adresse (le
   * doute que le message pointait est levé, #941). Aucun appelant actuel ne
   * le pose — canal conservé pour une garde de soumission future qui en
   * aurait besoin.
   */
  correctionHint: string | null;
  setCorrectionHint: (hint: string | null) => void;
  /**
   * Gate de soumission (#864, simplifiée #980, étendue #1141) : à appeler
   * depuis le submit handler du formulaire. Bloque sur un géocodage en vol
   * sur une adresse retouchée (`editedSinceLoad`), ET sur un verdict non
   * résolu (`!resolved`) même sans retouche — même critère que le bouton de
   * soumission de l'appelant (`isSaveDisabled`), en défense en profondeur
   * pour un `submit` qui contournerait ce bouton.
   */
  guardSubmit: (options: GuardSubmitOptions) => void;
  /**
   * Repart d'un état frais : purge géocodage, carte, hint et `payloadPin`,
   * puis re-géocode la saisie courante. Pour les appelants dont l'adresse
   * change de vie en cours de montage (toggle siège/départ #941, réouverture
   * de modale hôte). Le `manualPin` seedé DOIT être apparié aux champs que
   * l'appelant pose dans le même commit (batch React) — un texte qui ne
   * correspond pas au pin rend le gel immédiatement caduc.
   */
  reset: (options?: {manualPin?: ManualPinSeed | null}) => void;
};

/**
 * Machine à états de vérification d'adresse — géocodage IGN debounced, pin
 * manuel, message de correction, verdict de résolution (#980). Source
 * unique partagée entre le badge (`AddressGeocodeStatus`) et le bouton de
 * soumission du formulaire appelant (`resolved`) : le hook vit chez
 * l'appelant, le badge n'est qu'un rendu (#937 — deux copies du même état
 * tenues par deux écrivains finissent par diverger).
 */
export function useAddressVerification({
  fields,
  getGeocode,
  initialManualPin,
  enabled = true,
  onConfirmationEvent,
}: UseAddressVerificationOptions): AddressVerification {
  /**
   * Initializer LAZY : capture `initialManualPin` + les champs courants une
   * seule fois, au montage — symétrique du cas "posé cette session", capturé
   * au moment du drop dans `dropPin` (#929).
   */
  const [manualPin, setManualPin] = useState<ManualPinSeed | null>(() =>
    initialManualPin != null ? {position: initialManualPin, fields} : null
  );
  const [mapMode, setMapMode] = useState(false);
  const [mapCenter, setMapCenter] = useState<ManualPinPosition | null>(null);
  const [mapCenterPrecision, setMapCenterPrecision] = useState<MapCenterPrecision | null>(null);
  const [resolvingMapCenter, setResolvingMapCenter] = useState(false);
  const isManualFrozen = manualPin != null && !hasAddressChanged(manualPin.fields, fields);
  /**
   * Position à afficher dans la carte (#1011) — dérivée du RENDU courant, pas
   * d'un effet : `payloadPin` (plus bas) se met à jour un rendu plus tard
   * (état posé par un `useEffect`), ce qui ferait flasher le marqueur sur
   * l'ancienne position avant de corriger. Gardé par `isManualFrozen` :
   * `manualPin` n'est PAS purgé quand le texte est réédité (seul le gel
   * retombe, `isManualFrozen` redevient faux) — sans ce garde, rouvrir la
   * carte sur une nouvelle saisie afficherait un pin fantôme à des
   * kilomètres du nouveau `mapCenter`, hors du viewport.
   */
  const pinPosition = isManualFrozen ? (manualPin?.position ?? null) : null;

  const [correctionHint, setCorrectionHint] = useState<string | null>(null);

  // Incrémenté par `reset()` pour forcer un géocodage frais même à saisie
  // identique (réouverture de modale sur la même adresse).
  const [resetNonce, setResetNonce] = useState(0);

  const [rawGeocode, setRawGeocode] = useState<GeocodeResult | null>(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");

  // Gel manuel (#929, AC « sans re-géocodage ») : un pin actif épargne
  // l'appel IGN ; `enabled` couvre le cas hors écran (section repliée).
  const skip = !enabled || isManualFrozen;
  /**
   * `getGeocode` est lu via une ref et JAMAIS mis en deps de l'effet : un
   * appelant qui passerait une closure recréée à chaque rendu redémarrerait
   * sinon le debounce en boucle — le géocodage ne se poserait jamais, sans
   * aucune erreur visible. L'identité de la fonction n'est pas un signal :
   * seule la saisie (et `reset()`) redéclenche une vérification.
   */
  const getGeocodeRef = useRef(getGeocode);
  getGeocodeRef.current = getGeocode;
  /**
   * Champs pour lesquels `rawGeocode`/`error` sont valides — mis à jour
   * UNIQUEMENT dans l'effet ci-dessous, jamais pendant le rendu.
   * Sert de garde-fou anti-régression : `processing` ne devient `true` qu'au
   * PROCHAIN rendu après une frappe (l'effet n'a pas encore tourné), donc un
   * rendu intermédiaire calculerait `status` sur l'ANCIEN `rawGeocode` contre
   * la NOUVELLE saisie — un faux `close-match`/`not-found` d'un seul rendu,
   * suffisant pour émettre un `onConfirmationEvent("shown", …)` parasite à
   * chaque frappe. `fieldsStale` (plus bas) ferme cette fenêtre en traitant
   * tout écart comme `processing`, sans attendre l'effet.
   */
  const postedForFieldsRef = useRef(fields);
  /**
   * `true` dès qu'un géocodage RÉEL a été posté (branches `.then`/`.catch`
   * ci-dessous) — distinct de `postedForFieldsRef`, pré-seedé à `fields` au
   * montage même sans fetch. Guarde le cycle « poser un pin puis annuler »
   * (#1132/ex-#1020) : sans lui, `postedForFieldsRef` matcherait déjà la
   * saisie inchangée au premier passage et sauterait le tout premier
   * géocodage. Remis à `false` par `reset()` pour préserver son contrat
   * existant (forcer un géocodage frais à saisie identique sur réouverture
   * de modale).
   */
  const hasFetchedOnceRef = useRef(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies(resetNonce): dep volontairement surnuméraire — `reset()` force un géocodage frais à saisie identique (réouverture de modale).
  useEffect(() => {
    if (skip) {
      return;
    }
    /**
     * Objet stable référençant EXACTEMENT les 4 champs listés en deps
     * ci-dessous (jamais `fields` tel quel : biome refuse une capture plus
     * générique que les dépendances déclarées).
     */
    const requestedFields: AddressComparisonFields = {
      line1: fields.line1,
      line2: fields.line2,
      zip: fields.zip,
      city: fields.city,
    };
    if (!fields.line1 || !fields.zip || !fields.city) {
      setRawGeocode(null);
      /**
       * Saisie vide/incomplète : pas une TENTATIVE de géocodage ratée, rien à
       * signaler — une erreur d'un essai précédent ne doit pas survivre à un
       * champ vidé (`AddressGeocodeStatus` ne rendrait sinon plus rien de
       * cohérent : ni carte "introuvable" légitime, ni état vide).
       */
      setError("");
      postedForFieldsRef.current = requestedFields;
      return;
    }
    /**
     * Cycle « poser un pin puis annuler » sans retouche de texte (#1132/
     * ex-#1020) : la saisie déjà géocodée UNE fois ne mérite pas un second
     * appel IGN identique — évite de gaspiller le quota du throttle public
     * (20/min/IP) sur un simple aller-retour carte. `hasFetchedOnceRef`
     * (jamais `postedForFieldsRef` seul, pré-seedé sans fetch réel) isole le
     * cas d'un pin hydraté au montage : voir son commentaire ci-dessus pour
     * les cas limites couverts.
     */
    if (
      hasFetchedOnceRef.current &&
      !hasAddressChanged(postedForFieldsRef.current, requestedFields)
    ) {
      return;
    }

    setProcessing(true);
    setError("");
    // Une nouvelle vérification démarre : le message de correction portait
    // sur la saisie précédente.
    setCorrectionHint(null);
    let cancelled = false;

    const timer = setTimeout(() => {
      getGeocodeRef
        .current(fields.zip, fields.city, fields.line1, fields.line2 ?? undefined)
        .then((result) => {
          if (cancelled) {
            return;
          }
          if (result) {
            setRawGeocode(result);
          } else {
            setRawGeocode(null);
            setError("Adresse introuvable, vérifiez la saisie");
          }
          hasFetchedOnceRef.current = true;
          postedForFieldsRef.current = requestedFields;
          setProcessing(false);
        })
        .catch(() => {
          if (cancelled) {
            return;
          }
          setRawGeocode(null);
          /**
           * `hasFetchedOnceRef` reste `false` ici (contrairement à `.then`
           * ci-dessus) : un échec réseau n'est pas une résolution, la saisie
           * ne doit pas être considérée "déjà fetchée" pour le guard #1020 —
           * sinon retaper la même adresse (ou un aller-retour carte) après une
           * erreur ne relance jamais l'essai, sans porte de sortie visible.
           * Message toujours générique (jamais le texte brut de l'erreur,
           * souvent anglais/technique, ex. Axios "Request failed with status
           * code 400" sur un zip malformé) — même wording que l'ancien tunnel
           * pré-#980 (AddressStep.tsx).
           */
          setError("Une erreur est survenue. Merci de réessayer.");
          postedForFieldsRef.current = requestedFields;
          setProcessing(false);
        });
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      setProcessing(false);
    };
  }, [fields.line1, fields.line2, fields.zip, fields.city, skip, resetNonce]);

  /**
   * `fieldsStale` ne se referme QUE dans l'effet de géocodage (voir
   * `postedForFieldsRef` ci-dessus) — jamais tant que `skip` est vrai (pin
   * gelé ou `enabled: false`), puisque l'effet y retourne avant de toucher la
   * ref. Sans le `!skip` ci-dessous, un appelant qui bascule `enabled` à
   * `false` EN MÊME TEMPS qu'il change `fields` (ex. `verification.reset()` +
   * `setValue` d'un nouveau texte dans le même commit) resterait périmé pour
   * toujours — `guardSubmit` bloquerait la sauvegarde indéfiniment, aucune UI
   * ne pouvant plus jamais faire retomber `fieldsStale` à `false` puisque
   * l'effet qui le ferait ne tourne plus. Pendant `skip`, aucun géocodage
   * n'est attendu : il n'y a rien à rattraper.
   */
  const fieldsStale = hasAddressChanged(postedForFieldsRef.current, fields);
  const isCheckingOrStale = !skip && (processing || fieldsStale);
  const geocode = isCheckingOrStale ? null : rawGeocode;
  const reliable =
    geocode != null && isReliableGeocode(geocode.precision, geocode.latitude, geocode.longitude);

  /**
   * `structuredReplacement` (#980, étendu aux lieux-dits par #1082) : rue OU
   * lieu-dit + CP/ville prêts pour le bouton « Remplacer » — seulement quand
   * l'IGN a résolu une des 4 granularités éligibles ET que les composants
   * structurés sont bien présents et NON VIDES (`asString`, côté serveur,
   * conserve une chaîne vide telle quelle — un composant vide passerait donc
   * un simple test `!= null` et produirait un `line1` vide transmis tel quel
   * à `onReplace`, effaçant la saisie de l'utilisateur au clic « Remplacer »).
   * `line1` vient de `housenumber`+`street` recomposés sur une rue
   * (`structuredStreet`), ou de `resolvedName` tel quel sur un lieu-dit —
   * `locality`/`toponym` n'ont pas de composants rue à recomposer. Le
   * complément n'entre jamais dans ce trio : le géocodeur ne le connaît pas,
   * l'appelant qui applique `onReplace` doit le conserver.
   */
  const structuredStreet = [geocode?.housenumber, geocode?.street]
    .filter((part): part is string => Boolean(part?.trim()))
    .join(" ");
  const hasStructuredStreet =
    geocode != null &&
    (geocode.precision === "housenumber" || geocode.precision === "street") &&
    !!geocode.postcode?.trim() &&
    !!geocode.city?.trim() &&
    structuredStreet !== "";
  const hasStructuredPlace =
    geocode != null &&
    (geocode.precision === "locality" || geocode.precision === "toponym") &&
    !!geocode.postcode?.trim() &&
    !!geocode.city?.trim() &&
    !!geocode.resolvedName.trim();
  const hasStructuredResult = hasStructuredStreet || hasStructuredPlace;
  const structuredReplacement: StructuredReplacementFields | null = hasStructuredResult
    ? {
        line1: hasStructuredStreet ? structuredStreet : (geocode?.resolvedName ?? ""),
        zip: geocode?.postcode ?? "",
        city: geocode?.city ?? "",
      }
    : null;

  /**
   * Concordance CP/ville (#980) : le containment/l'égalité comparés plus bas
   * ne portent QUE sur la rue ou le lieu-dit par construction (doctrine
   * `@mytour/shared`, "zip/ville ne rentrent JAMAIS dans ce containment") — un
   * géocode qui trouve le MÊME nom dans une AUTRE commune (mock-up validé,
   * cadre 5 : "8 rue des Lilas" 69003 Lyon → "8 Rue des Lilas" 69120
   * Vaulx-en-Velin) passerait donc pour "reconnue" sans ce garde dédié —
   * exactement le cas que le callout existe pour attraper.
   */
  const zipCityConcordant =
    hasStructuredResult &&
    normalizeAddressComponent(geocode?.postcode ?? "") === normalizeAddressComponent(fields.zip) &&
    normalizeAddressComponent(geocode?.city ?? "") === normalizeAddressComponent(fields.city);

  const saisieText = buildGeocodeInputText(fields);
  /**
   * Concordance saisie ↔ résolu (#1082) :
   * - rue (`housenumber`/`street`) : containment `needsGeocodeConfirmation`,
   *   inchangé depuis #920, comparé à `saisieText` (ligne1+complément — le
   *   containment tolère une saisie plus riche que le résolu, complément
   *   compris).
   * - lieu-dit (`locality`/`toponym`) : containment `needsGeocodeConfirmation`
   *   (déjà applicable à ces deux granularités depuis #920, sur `saisieText`
   *   comme la rue) OU égalité exacte texte normalisé (`isExactLocalityMatch`).
   *   La seconde voie est nécessaire parce que le containment confirme
   *   TOUJOURS un lieu-dit à un seul mot distinctif ("Le Bourg"), même sur
   *   une correspondance exacte (garde mono-token de `needsGeocodeConfirmation`,
   *   intact) — sans ce bypass ciblé, "La Thiolais" saisi ≡ "La Thiolais"
   *   résolu resterait indéfiniment "close-match" faute d'assez de tokens
   *   pour le containment.
   *   ⚠️ Comparée à `fields.line1` SEUL, jamais `saisieText` : contrairement
   *   au containment, une égalité EXACTE ne tolère aucun texte en plus — un
   *   complément non vide (`line2`) ferait toujours échouer l'égalité contre
   *   `saisieText`, bloquant `"close-match"` à vie sur un lieu-dit mono-token
   *   avec complément (`"Remplacer"` ne réécrit jamais `line2`, trouvé en
   *   durcissement de #1082).
   */
  const addressConcordant =
    geocode != null &&
    (isExactLocalityMatch(geocode.precision, geocode.resolvedName, fields.line1) ||
      !needsGeocodeConfirmation(geocode.precision, geocode.resolvedName, saisieText));

  /**
   * Verdict de résolution (#980, élargi aux lieux-dits par #1082) — taxonomie
   * à 4 états visibles :
   * 1. Pin manuel gelé : toujours résolu, prioritaire (jamais re-géocodé, #916).
   * 2. Géocodage en vol : transitoire, jamais résolu.
   * 3. Precision hors des 4 granularités éligibles (`municipality`/`unknown`
   *    retombent en `not-found`, même si `isReliableGeocode` les jugerait
   *    exploitables — décision produit assumée, voir le commentaire sur
   *    `reliable`).
   * 4. Granularité éligible SANS composants structurés exploitables
   *    (`!hasStructuredResult`, payload malformé résiduel) : rien à proposer
   *    dans un callout — introuvable plutôt qu'un "proche trouvée" sans bouton.
   * 5. Granularité éligible AVEC composants structurés : reconnue si
   *    `addressConcordant` ET CP/ville concordent, sinon proche trouvée.
   * 6. Tout le reste (0 résultat compris, `geocode == null`) : introuvable.
   */
  const status: AddressResolutionStatus = isManualFrozen
    ? "manual-pin"
    : isCheckingOrStale
      ? "checking"
      : geocode == null
        ? "not-found"
        : geocode.precision !== "housenumber" &&
            geocode.precision !== "street" &&
            geocode.precision !== "locality" &&
            geocode.precision !== "toponym"
          ? "not-found"
          : !hasStructuredResult
            ? "not-found"
            : addressConcordant && zipCityConcordant
              ? "recognized"
              : "close-match";
  const resolved = status === "recognized" || status === "manual-pin";

  /**
   * Télémétrie du callout (#980, re-mappe `geocode_confirmation_shown`) :
   * émise à l'ENTRÉE dans `close-match`, pas à chaque rendu — symétrique de
   * l'ancien déclenchement "à l'ouverture de la modale". Lue via ref, jamais
   * mise en deps de l'identité (même raison que `getGeocodeRef`).
   */
  const onConfirmationEventRef = useRef(onConfirmationEvent);
  onConfirmationEventRef.current = onConfirmationEvent;
  const previousStatusRef = useRef(status);
  /**
   * Garde `enabled` (#1124), PAS `skip` : `isCheckingOrStale` (ci-dessus) est
   * forcé `false` sous `skip` (= `!enabled || isManualFrozen`), ce qui
   * démasque `rawGeocode` UNIQUEMENT dans le cas suspension — un texte resté
   * en vol au moment où `enabled` passe à `false` peut alors retomber sur un
   * ancien résultat et transiter vers `close-match` sans géocodage réel. Le
   * cas pin gelé (`isManualFrozen`) n'a pas ce problème : `status` y vaut
   * déterministement `"manual-pin"`, jamais une lecture périmée de
   * `rawGeocode` — un garde sur `skip` y gèlerait `previousStatusRef` à tort
   * et perdrait la transition réelle au retrait du pin sans retouche de
   * texte. Sans le garde `enabled`, la transition fantôme sous suspension
   * émettrait un `shown` parasite ; `previousStatusRef` n'avance pas non
   * plus tant qu'`enabled` est faux, pour que la VRAIE transition, une fois
   * le hook réactivé, reste détectable contre l'état d'avant suspension.
   */
  useEffect(() => {
    if (!enabled) {
      return;
    }
    if (status === "close-match" && previousStatusRef.current !== "close-match") {
      onConfirmationEventRef.current?.("shown", geocode?.precision ?? null);
    }
    previousStatusRef.current = status;
  }, [status, geocode?.precision, enabled]);

  // Un pin qui gèle lève le doute que le message pointait (#941).
  useEffect(() => {
    if (isManualFrozen) {
      setCorrectionHint(null);
    }
  }, [isManualFrozen]);

  /**
   * `payloadPin` — garde-fou anti-régression, le montage est SAUTÉ exprès :
   * 1. Au montage, l'état ne fait que refléter `initialManualPin`, déjà connu
   *    de l'appelant qui l'a fourni — notifier quand même enverrait un `null`
   *    explicite ("dé-pin", #799) pour TOUT client jamais marqué manuel, à
   *    chaque enregistrement.
   * 2. Seuls les changements POST-montage/POST-reset (drop, retrait, texte
   *    réédité) sont une information nouvelle à écrire dans le payload.
   * 3. Le saut se fait par comparaison de VALEUR à la dernière valeur actée
   *    (pas par saut du premier run) : un `reset()` qui ne change rien ne
   *    laisse ainsi jamais un vrai changement suivant se faire avaler.
   */
  const [payloadPin, setPayloadPin] = useState<ManualPinPosition | null | undefined>(undefined);
  const lastAppliedPinRef = useRef<{frozen: boolean; position: ManualPinPosition | null}>({
    frozen: initialManualPin != null,
    position: initialManualPin ?? null,
  });
  useEffect(() => {
    const current = {
      frozen: isManualFrozen,
      position: isManualFrozen && manualPin ? manualPin.position : null,
    };
    const last = lastAppliedPinRef.current;
    if (current.frozen === last.frozen && positionsEqual(current.position, last.position)) {
      return;
    }
    lastAppliedPinRef.current = current;
    setPayloadPin(current.position);
  }, [isManualFrozen, manualPin]);

  /**
   * Actions à identité STABLE (deps `[]`, lectures via refs) : les appelants
   * les mettent en deps d'effets (hydratation de formulaire) — une identité
   * qui change à chaque rendu les re-déclencherait en boucle.
   */
  const fieldsRef = useRef(fields);
  fieldsRef.current = fields;
  const geocodeRef = useRef(geocode);
  geocodeRef.current = geocode;

  const structuredReplacementRef = useRef(structuredReplacement);
  structuredReplacementRef.current = structuredReplacement;

  /**
   * Télémétrie du clic « Remplacer » (#980) — la réécriture des champs RHF
   * elle-même est faite par l'appelant, via la prop `onReplace` de
   * `AddressVerificationSection`/`AddressGeocodeStatus` (jouée au même clic,
   * juste à côté de cet appel — voir leur JSX).
   */
  const replace = useCallback(() => {
    if (!structuredReplacementRef.current) {
      return;
    }
    onConfirmationEventRef.current?.("confirmed", geocodeRef.current?.precision ?? null);
  }, []);

  /**
   * Le géocode COURANT (même peu fiable) sert de centre initial, aucun appel
   * réseau supplémentaire (#929). En échec TOTAL (aucun géocode posé — 0
   * résultat IGN, `status: "not-found"` sans `geocode`) : géocode en secours
   * la seule commune via `getGeocode(zip, city, city)` (query = le nom de
   * ville, pas de rue) — approximation best-effort, PAS la borne de
   * cohérence stricte (`fetchCommuneCentroid`, serveur, #799) — pour quand
   * même pouvoir cadrer la carte. Reste no-op si zip/ville manquent (aucun
   * centre à cadrer). Si même ce géocodage de secours échoue (0 résultat ou
   * exception), la carte s'ouvre quand même sur `DEFAULT_MAP_CENTER` — le pin
   * manuel est la porte de sortie de l'adresse introuvable (#799), elle ne
   * doit jamais être bloquée par un échec de géocodage (#1014). Ce repli
   * n'est plus un état d'erreur (`error` reste celui déjà posé par l'effet de
   * géocodage, ex. "Adresse introuvable") : il ne se substitue à aucun
   * message plus précis.
   */
  /**
   * Snapshot du `manualPin` à l'ENTRÉE en mode carte (#1011) — restauré par
   * `closeMap()` : l'abandon de la session carte n'engage rien, même après
   * plusieurs `dropPin()` intermédiaires. Lu/écrit via ref (comme
   * `fieldsRef`/`geocodeRef`) car `openMap`/`closeMap` ont une identité
   * stable (deps `[]`).
   */
  const manualPinRef = useRef(manualPin);
  manualPinRef.current = manualPin;
  const mapEntryPinRef = useRef<ManualPinSeed | null>(null);

  const openMap = useCallback(async () => {
    /**
     * Snapshot posé une seule fois en tête : toute branche qui ouvre la
     * carte (`setMapMode(true)`) doit en disposer, une affectation dupliquée
     * par branche est un oubli en attente.
     */
    mapEntryPinRef.current = manualPinRef.current;
    const current = geocodeRef.current;
    if (current) {
      setMapCenter({lat: current.latitude, lng: current.longitude});
      setMapCenterPrecision("geocode");
      setMapMode(true);
      return;
    }
    const requestedFields = fieldsRef.current;
    const {zip, city} = requestedFields;
    if (!zip || !city) {
      return;
    }
    setResolvingMapCenter(true);
    try {
      const communeGeocode = await getGeocodeRef.current(zip, city, city);
      /**
       * Périmé : la saisie a changé pendant le géocodage de secours — ouvrir
       * la carte centrerait sur une commune qui ne correspond plus au récap
       * affiché (`fields` passé à `ManualPinPlacement`), et un pin déposé
       * engagerait ces coordonnées périmées sur la NOUVELLE saisie.
       * Silencieux : la saisie a bougé, l'utilisateur n'attend plus ce
       * résultat précis. Même garde appliquée au `catch` ci-dessous — une
       * requête qui échoue tardivement est tout aussi périmée qu'une qui
       * résout tardivement.
       */
      if (hasAddressChanged(requestedFields, fieldsRef.current)) {
        return;
      }
      if (!communeGeocode) {
        const fallback = resolveDepartmentOrDefaultMapCenter(zip);
        setMapCenter(fallback.center);
        setMapCenterPrecision(fallback.precision);
        setMapMode(true);
        return;
      }
      setMapCenter({lat: communeGeocode.latitude, lng: communeGeocode.longitude});
      setMapCenterPrecision("commune");
      setMapMode(true);
    } catch {
      if (hasAddressChanged(requestedFields, fieldsRef.current)) {
        return;
      }
      const fallback = resolveDepartmentOrDefaultMapCenter(zip);
      setMapCenter(fallback.center);
      setMapCenterPrecision(fallback.precision);
      setMapMode(true);
    } finally {
      setResolvingMapCenter(false);
    }
  }, []);

  const closeMap = useCallback(() => {
    setManualPin(mapEntryPinRef.current);
    setMapMode(false);
  }, []);

  const dropPin = useCallback((position: ManualPinPosition) => {
    setManualPin({position, fields: fieldsRef.current});
  }, []);

  const removePin = useCallback(() => {
    setManualPin(null);
  }, []);

  const reset = useCallback((options?: {manualPin?: ManualPinSeed | null}) => {
    const seed = options?.manualPin ?? null;
    setManualPin(seed);
    setMapMode(false);
    setMapCenter(null);
    setMapCenterPrecision(null);
    setCorrectionHint(null);
    setRawGeocode(null);
    setError("");
    lastAppliedPinRef.current = {frozen: seed != null, position: seed?.position ?? null};
    setPayloadPin(undefined);
    hasFetchedOnceRef.current = false;
    setResetNonce((nonce) => nonce + 1);
  }, []);

  /**
   * Gate de soumission (#864, simplifiée #980, étendue #1141) — closure du
   * rendu courant (appelée depuis un event handler, jamais mise en deps
   * d'effet) :
   * 1. Géocodage en vol sur une adresse retouchée (`editedSinceLoad`) — le
   *    verdict n'existe pas encore.
   * 2. Verdict non résolu (`!resolved`) SUR UN HOOK ACTIF (`enabled`),
   *    retouchée ou pas — même critère que `isSaveDisabled` chez l'appelant.
   *    Sans cette branche, une adresse hydratée jamais retouchée et restée
   *    "introuvable" ne tenait que par le `disabled` du bouton : un `submit`
   *    qui le contourne atteignait `onPass` (#1141). Gardé par `enabled` :
   *    `resolved` reste dérivé à chaque rendu même sous `skip` (#1124) — il
   *    PEUT bouger pendant la suspension (cf. le garde `skip` de l'effet de
   *    télémétrie ci-dessus) — mais cette branche reste permissive tant que
   *    `enabled` est faux, en miroir de la branche 1 (`!skip` sur
   *    `isCheckingOrStale`) : un hook suspendu n'a plus de géocodage qui
   *    tourne pour faire évoluer le verdict vers `resolved`, bloquer dessus
   *    reviendrait à bloquer indéfiniment un consommateur suspendu.
   */
  function guardSubmit({editedSinceLoad = true, onPass}: GuardSubmitOptions) {
    if (isCheckingOrStale && editedSinceLoad) {
      return;
    }
    if (enabled && !resolved) {
      return;
    }
    onPass();
  }

  return {
    fields,
    geocode,
    processing,
    error,
    reliable,
    status,
    resolved,
    structuredReplacement,
    replace,
    isManualFrozen,
    mapMode,
    mapCenter,
    mapCenterPrecision,
    pinPosition,
    openMap,
    resolvingMapCenter,
    closeMap,
    dropPin,
    removePin,
    payloadPin,
    correctionHint,
    setCorrectionHint,
    guardSubmit,
    reset,
  };
}

function positionsEqual(a: ManualPinPosition | null, b: ManualPinPosition | null): boolean {
  if (a == null || b == null) {
    return a === b;
  }
  return a.lat === b.lat && a.lng === b.lng;
}

/**
 * Comparaison best-effort, PAS un critère de containment texte (celui-ci
 * reste `needsGeocodeConfirmation`) — délègue au nettoyage bas niveau partagé
 * `normalizeAddressText` (voir sa doc pour l'invariant de partage). Exportée
 * et réutilisée telle quelle par `AddressCloseMatch` (décision de
 * surlignage) : un même segment doit être jugé concordant par les DEUX.
 */
export function normalizeAddressComponent(value: string): string {
  return normalizeAddressText(value);
}
